'use client';

import * as React from 'react';
import { Moon, Sun, Desktop, Check } from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function ModeToggle() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  // SSR/hydration: render a placeholder until we know the resolved theme.
  // Avoids the brief flash where the wrong icon shows for one frame.
  const showSun = mounted ? (resolvedTheme ?? 'dark') === 'light' : false;

  const choices = [
    { value: 'light', label: 'Light',   Icon: Sun },
    { value: 'dark',  label: 'Dark',    Icon: Moon },
    { value: 'system', label: 'System', Icon: Desktop },
  ] as const;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 rounded-lg px-0 transition-colors hover:bg-muted/60"
        >
          {/* Smoother icon swap — fade + scale on theme change */}
          <span className="relative inline-flex size-[1.1rem] items-center justify-center">
            <Sun
              weight="duotone"
              className={`absolute size-[1.1rem] transition-all duration-300 ease-out ${
                showSun ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-75 -rotate-90'
              }`}
            />
            <Moon
              weight="duotone"
              className={`absolute size-[1.1rem] transition-all duration-300 ease-out ${
                showSun ? 'opacity-0 scale-75 rotate-90' : 'opacity-100 scale-100 rotate-0'
              }`}
            />
          </span>
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[8rem] p-1">
        {choices.map(({ value, label, Icon }) => {
          const active = (theme ?? 'system') === value;
          return (
            <DropdownMenuItem
              key={value}
              onClick={() => setTheme(value)}
              className="flex items-center gap-2 rounded-md text-sm cursor-pointer"
            >
              <Icon weight="duotone" className="size-4 text-violet-300" />
              <span className="flex-1">{label}</span>
              {active && <Check weight="bold" className="size-3.5 text-violet-400" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
